package com.patronat.top.client.presentation.home

import com.patronat.top.client.presentation.home.model.HomeCategoryShortcut
import com.patronat.top.client.location.ClientGeoLocation
import com.patronat.top.client.location.ClientLocationSuggestion
import com.patronat.top.core.base.ViewEvent
import com.patronat.top.core.base.ViewSideEffect
import com.patronat.top.core.base.ViewState
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.dto.ReviewSummaryResponse
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueGroup
import com.patronat.top.library.media.data.FileSource

data class ClientHomeViewState(
    val currentScreen: ClientHomeScreen = ClientHomeScreen.Feed,
    val isLoading: Boolean = false,
    val searchQuery: String = "",
    val selectedGroup: VenueGroup? = null,
    val selectedCategory: VenueCategory? = null,
    val userLocation: ClientGeoLocation? = null,
    val selectedSessionLocation: ClientLocationSuggestion? = null,
    val currentLocationTitle: String? = null,
    val filterLocation: ClientVenueLocationFilter = ClientVenueLocationFilter.Around,
    val filterPriceBounds: IntRange = 0..0,
    val filterPriceRange: IntRange = 0..0,
    val isPriceFilterChanged: Boolean = false,
    val filterRatingRange: IntRange = 3..8,
    val isFilterApplied: Boolean = false,
    val serviceSearchQuery: String = "",
    val serviceSuggestions: List<String> = emptyList(),
    val selectedServiceQueries: List<String> = emptyList(),
    val locationSearchQuery: String = "",
    val locationSuggestions: List<ClientLocationSuggestion> = emptyList(),
    val isLocationSearchLoading: Boolean = false,
    val categorySearchQuery: String = "",
    val expandedCategoryGroups: Set<VenueGroup> = setOf(VenueGroup.FOOD),
    val allVenues: List<VenueResponse> = emptyList(),
    val venues: List<VenueResponse> = emptyList(),
    val favoriteVenueIds: Set<String> = emptySet(),
    val selectedVenueId: String? = null,
    val venueReviews: List<ReviewResponse> = emptyList(),
    val venueReviewSummary: ReviewSummaryResponse? = null,
    val isVenueReviewsLoading: Boolean = false,
    val selectedReviewPhotoUrl: String? = null,
    val likingReviewIds: Set<String> = emptySet(),
    val reviewForm: ClientReviewFormState = ClientReviewFormState()
) : ViewState

enum class ClientHomeScreen {
    Feed,
    LocationSearch,
    AllCategories,
    Filters,
    VenueDetails,
    VenueServices,
    VenueReviews,
    VenueReview
}

enum class ClientVenueLocationFilter {
    Around,
    Within40Km,
    Over40Km
}

data class ClientReviewFormState(
    val overallRating: Int = 0,
    val priceRating: Int? = null,
    val qualityRating: Int? = null,
    val speedRating: Int? = null,
    val staffRating: Int? = null,
    val atmosphereRating: Int? = null,
    val text: String = "",
    val photos: List<ClientReviewPhoto> = emptyList(),
    val isPhotoUploading: Boolean = false,
    val isSubmitting: Boolean = false
)

data class ClientReviewPhoto(
    val mediaId: String,
    val publicUrl: String
)

data class ClientPickedFile(
    val fileSource: FileSource,
    val mimeType: String,
    val fileName: String
)

enum class ClientReviewMetric {
    Price,
    Quality,
    Speed,
    Staff,
    Atmosphere
}

sealed interface ClientHomeEvent : ViewEvent {
    data object OnRefresh : ClientHomeEvent
    data class OnSearchChanged(val value: String) : ClientHomeEvent
    data class OnHomeCategoryClicked(val category: HomeCategoryShortcut) : ClientHomeEvent
    data object OnLocationSelectorClicked : ClientHomeEvent
    data object OnCloseLocationSearchClicked : ClientHomeEvent
    data class OnLocationSearchChanged(val value: String) : ClientHomeEvent
    data class OnLocationSuggestionClicked(val value: ClientLocationSuggestion) : ClientHomeEvent
    data object OnFilterClicked : ClientHomeEvent
    data object OnCloseFiltersClicked : ClientHomeEvent
    data class OnFilterLocationClicked(val value: ClientVenueLocationFilter) : ClientHomeEvent
    data class OnFilterPriceRangeChanged(val value: IntRange) : ClientHomeEvent
    data class OnFilterRatingRangeChanged(val value: IntRange) : ClientHomeEvent
    data class OnServiceSearchChanged(val value: String) : ClientHomeEvent
    data class OnServiceSuggestionClicked(val value: String) : ClientHomeEvent
    data class OnSelectedServiceRemoved(val value: String) : ClientHomeEvent
    data object OnApplyFiltersClicked : ClientHomeEvent
    data object OnViewAllCategoriesClicked : ClientHomeEvent
    data object OnBackToHomeClicked : ClientHomeEvent
    data class OnCategorySearchChanged(val value: String) : ClientHomeEvent
    data class OnCategoryGroupClicked(val group: VenueGroup) : ClientHomeEvent
    data class OnVenueCategoryClicked(val category: VenueCategory) : ClientHomeEvent
    data class OnVenueClicked(val venueId: String) : ClientHomeEvent
    data class OnVenueDeepLinkOpened(val venueId: String) : ClientHomeEvent
    data object OnVenueDetailsBackClicked : ClientHomeEvent
    data object OnVenueCatalogClicked : ClientHomeEvent
    data object OnVenueCatalogBackClicked : ClientHomeEvent
    data object OnVenueReviewsClicked : ClientHomeEvent
    data object OnVenueReviewsBackClicked : ClientHomeEvent
    data class OnVenueReviewPhotoClicked(val url: String) : ClientHomeEvent
    data object OnVenueReviewPhotoDismissed : ClientHomeEvent
    data class OnVenueReviewLikeClicked(val reviewId: String) : ClientHomeEvent
    data class OnFavoriteClicked(val venueId: String) : ClientHomeEvent
    data object OnVenueReviewClicked : ClientHomeEvent
    data object OnVenueReviewBackClicked : ClientHomeEvent
    data class OnReviewOverallRatingChanged(val value: Int) : ClientHomeEvent
    data class OnReviewDetailRatingChanged(
        val metric: ClientReviewMetric,
        val value: Int?
    ) : ClientHomeEvent
    data class OnReviewTextChanged(val value: String) : ClientHomeEvent
    data object OnReviewPhotoAddClicked : ClientHomeEvent
    data class OnReviewPhotoPicked(val file: ClientPickedFile) : ClientHomeEvent
    data class OnReviewPhotoRemoved(val mediaId: String) : ClientHomeEvent
    data object OnSubmitReviewClicked : ClientHomeEvent
}

sealed interface ClientHomeEffect : ViewSideEffect {
    data object OpenReviewPhotoPicker : ClientHomeEffect
}
